import { createReducer, on } from '@ngrx/store';
import { AuthenticatedUser } from '../../models/authenticated-user.model';
import { UserRole } from '../../services/role.service';
import { AuthApiActions, AuthActions, LoginPageActions, ChangePasswordPageActions } from './auth.actions';
import { TwoFactorSetupApiActions, TwoFactorSetupActions } from './auth.actions';
import { TwoFactorSetupResponse } from '../../services/two-factor.service';

export interface AuthState {
  readonly user: AuthenticatedUser | null;
  readonly token: string | null;
  readonly roles: readonly UserRole[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly twoFactorVerified: boolean;
  readonly isTwoFactorEnabled: boolean;
  readonly pendingTwoFactorChallengeId: string | null;
  readonly pendingTwoFactorEmail: string | null;
  readonly pendingChangePasswordEmail: string | null;
  readonly sessionInvalidatedAt: string | null;
  readonly twoFactorSetup: TwoFactorSetupResponse | null;
  readonly recoveryCodes: readonly string[];
  readonly twoFactorSetupLoading: boolean;
  readonly twoFactorSetupError: string | null;
}

export const initialAuthState: AuthState = {
  user: null,
  token: null,
  roles: [],
  isLoading: false,
  error: null,
  twoFactorVerified: false,
  isTwoFactorEnabled: false,
  pendingTwoFactorChallengeId: null,
  pendingTwoFactorEmail: null,
  pendingChangePasswordEmail: null,
  sessionInvalidatedAt: null,
  twoFactorSetup: null,
  recoveryCodes: [],
  twoFactorSetupLoading: false,
  twoFactorSetupError: null,
};

export const authReducer = createReducer(
  initialAuthState,
  on(LoginPageActions.loginRequested, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AuthApiActions.loginSucceeded, (state, payload) => ({
    ...state,
    user: payload.user,
    token: payload.token,
    roles: payload.roles,
    isLoading: false,
    error: null,
    isTwoFactorEnabled: payload.isTwoFactorEnabled,
    twoFactorVerified: false,
    sessionInvalidatedAt: null,
    pendingTwoFactorChallengeId: null,
    pendingTwoFactorEmail: null,
  })),
  on(AuthApiActions.loginFailed, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(AuthApiActions.twoFactorChallengeRequired, (state, { challengeId, email }) => ({
    ...state,
    isLoading: false,
    error: null,
    token: null,
    pendingTwoFactorChallengeId: challengeId,
    pendingTwoFactorEmail: email,
  })),
  on(AuthApiActions.mustChangePasswordRequired, (state, { email }) => ({
    ...state,
    isLoading: false,
    error: null,
    token: null,
    pendingChangePasswordEmail: email,
  })),
  on(ChangePasswordPageActions.changePasswordRequested, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AuthApiActions.changePasswordSucceeded, (state) => ({
    ...state,
    isLoading: false,
    error: null,
    pendingChangePasswordEmail: null,
  })),
  on(AuthApiActions.changePasswordFailed, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(AuthApiActions.refreshSucceeded, (state, { token }) => ({
    ...state,
    token,
    error: null,
  })),
  on(AuthApiActions.refreshFailed, (state, { error }) => ({ ...state, error })),
  on(AuthActions.appRehydrated, (state, payload) => ({
    ...state,
    user: payload.user,
    token: payload.token,
    roles: payload.roles,
    isTwoFactorEnabled: payload.isTwoFactorEnabled,
    twoFactorVerified: payload.twoFactorVerified,
    error: null,
  })),
  on(AuthActions.twoFactorVerificationRequested, (state) => ({ ...state, isLoading: true, error: null })),
  on(AuthApiActions.twoFactorVerificationSucceeded, (state) => ({
    ...state,
    isLoading: false,
    twoFactorVerified: true,
    error: null,
  })),
  on(AuthApiActions.twoFactorVerificationFailed, (state, { error }) => ({ ...state, isLoading: false, error })),
  on(AuthApiActions.logoutCompleted, () => initialAuthState),
  on(AuthActions.appRehydrationFailed, (state, { error }) => ({ ...state, error })),
  on(AuthActions.statePatched, (state, patch) => ({
    ...state,
    ...(patch.user !== undefined ? { user: patch.user } : {}),
    ...(patch.token !== undefined ? { token: patch.token } : {}),
    ...(patch.roles !== undefined ? { roles: patch.roles } : {}),
    ...(patch.isLoading !== undefined ? { isLoading: patch.isLoading } : {}),
    ...(patch.twoFactorVerified !== undefined ? { twoFactorVerified: patch.twoFactorVerified } : {}),
    ...(patch.isTwoFactorEnabled !== undefined ? { isTwoFactorEnabled: patch.isTwoFactorEnabled } : {}),
    ...(patch.sessionInvalidatedAt !== undefined ? { sessionInvalidatedAt: patch.sessionInvalidatedAt } : {}),
  })),
  on(
    TwoFactorSetupActions.enableRequested,
    TwoFactorSetupActions.confirmRequested,
    TwoFactorSetupActions.disableRequested,
    TwoFactorSetupActions.regenerateRequested,
    (state) => ({ ...state, twoFactorSetupLoading: true, twoFactorSetupError: null }),
  ),
  on(TwoFactorSetupApiActions.enableSucceeded, (state, { response }) => ({ ...state, twoFactorSetup: response, twoFactorSetupLoading: false, twoFactorSetupError: null })),
  on(TwoFactorSetupApiActions.confirmSucceeded, (state, { recoveryCodes }) => ({ ...state, recoveryCodes, isTwoFactorEnabled: true, twoFactorSetupLoading: false, twoFactorSetupError: null })),
  on(TwoFactorSetupApiActions.disableSucceeded, (state) => ({ ...state, isTwoFactorEnabled: false, twoFactorVerified: false, twoFactorSetupLoading: false, twoFactorSetupError: null })),
  on(TwoFactorSetupApiActions.regenerateSucceeded, (state, { recoveryCodes }) => ({ ...state, recoveryCodes, twoFactorSetupLoading: false, twoFactorSetupError: null })),
  on(
    TwoFactorSetupApiActions.enableFailed,
    TwoFactorSetupApiActions.confirmFailed,
    TwoFactorSetupApiActions.disableFailed,
    TwoFactorSetupApiActions.regenerateFailed,
    (state, { error }) => ({ ...state, twoFactorSetupLoading: false, twoFactorSetupError: error }),
  ),
);